<script setup lang="ts">
import type { IHistoryLog } from '@/types/IHistoryLog'
import { formatLongDateTime, formatRelativeTime, formatTimeOnly, getFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { useReactiveNow } from '@/utils/useReactiveNow'

defineProps<{
  logs: IHistoryLog[]
}>()

const emit = defineEmits<{
  (e: 'click-user', id: string): void
}>()

const now = useReactiveNow()

/** Стабильный fallback-аватар на пользователя в рамках сессии компонента */
const fallbackAvatars = new Map<string, string>()

function getUserAvatar(user: NonNullable<IHistoryLog['created_by']>): string {
  if (user.avatar)
    return user.avatar

  if (!fallbackAvatars.has(user.id))
    fallbackAvatars.set(user.id, getRandomAvatar())

  return fallbackAvatars.get(user.id)!
}

function getDotColor(actionDisplay: string): string {
  const action = actionDisplay.toLowerCase()

  if (action.includes('создан') || action.includes('отчет') || action.includes('отчёт'))
    return 'success'
  if (action.includes('удал') || action.includes('замечан'))
    return 'warning'
  if (action.includes('обнов'))
    return 'primary'

  return 'info'
}

function getLogTitle(log: IHistoryLog): string {
  return log.action_display || '—'
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
}

/**
 * История:
 * - сегодня (< 24 ч) → «N мин/ч назад»
 * - вчера → «Вчера в 11:10»
 * - старше → «14 июня 2024, 17:38»
 */
function formatHistoryTime(iso: string | null | undefined, currentNow: number): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const nowDate = new Date(currentNow)
  const yesterday = new Date(nowDate)
  yesterday.setDate(yesterday.getDate() - 1)

  if (isSameCalendarDay(date, yesterday)) {
    const time = formatTimeOnly(date)
    return time ? `Вчера в ${time}` : 'Вчера'
  }

  if (isSameCalendarDay(date, nowDate) || (currentNow - date.getTime()) < 24 * 60 * 60 * 1000)
    return formatRelativeTime(iso, currentNow)

  return formatLongDateTime(iso)
}

function formatChangeValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '')
    return '—'
  return value
}

function isEmptyOldValue(value: string | null | undefined): boolean {
  return value === null || value === undefined || value === '' || value === 'Пусто'
}

function hasChanges(log: IHistoryLog): boolean {
  return !!log.changes_display && Object.keys(log.changes_display).length > 0
}
</script>

<template>
  <div class="entity-form-scroll-body">
    <div
      v-if="logs.length === 0"
      class="text-center text-medium-emphasis py-4 text-body-2"
    >
      История изменений пуста
    </div>

    <VTimeline
      v-else
      side="end"
      align="start"
      density="compact"
      truncate-line="both"
      line-inset="8"
      class="w-100"
    >
      <VTimelineItem
        v-for="log in logs"
        :key="log.id"
        :dot-color="getDotColor(log.action_display)"
        size="x-small"
      >
        <div class="d-flex flex-column ga-2">
          <div class="d-flex flex-wrap align-center justify-space-between ga-2">
            <span class="text-body-1 font-weight-medium text-high-emphasis">
              {{ getLogTitle(log) }}
            </span>
            <span class="text-caption text-disabled">
              {{ formatHistoryTime(log.created_at, now) }}
            </span>
          </div>

          <div
            v-if="log.created_by"
            class="d-flex align-center ga-2 cursor-pointer"
            @click="emit('click-user', log.created_by.id)"
          >
            <VAvatar
              size="32"
              :image="getUserAvatar(log.created_by)"
            />
            <div class="d-flex flex-column">
              <span class="text-body-2 font-weight-medium text-high-emphasis">
                {{ getFullName(log.created_by) }}
              </span>
              <span
                v-if="log.created_by.position_name"
                class="text-body-2 text-medium-emphasis"
              >
                {{ log.created_by.position_name }}
              </span>
            </div>
          </div>
          <span
            v-else
            class="text-body-2 text-medium-emphasis"
          >
            Система
          </span>

          <div class="d-flex flex-column ga-2">
            <span
              v-if="log.object_display || log.object_repr"
              class="text-body-1 font-weight-medium text-high-emphasis"
            >
              {{ log.object_display || log.object_repr }}
            </span>

            <CustomDivider v-if="(log.object_display || log.object_repr) && hasChanges(log)" />

            <div
              v-if="hasChanges(log)"
              class="d-flex flex-column ga-1"
            >
              <div
                v-for="(change, field) in log.changes_display"
                :key="field"
                class="d-flex flex-wrap align-center ga-1"
              >
                <span class="text-body-2 font-weight-medium text-high-emphasis">
                  {{ field }}
                </span>
                <span class="text-body-2 text-medium-emphasis text-break">
                  <template v-if="isEmptyOldValue(change[0])">
                    {{ formatChangeValue(change[1]) }}
                  </template>
                  <template v-else>
                    {{ formatChangeValue(change[0]) }} → {{ formatChangeValue(change[1]) }}
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>
      </VTimelineItem>
    </VTimeline>
  </div>
</template>
